// src/lib/inbox-preview.js — dérivation d'EXTRAIT pour les listes de l'Inbox
// (micro-lot admin, décision Dimitri 2026-08-01).
//
// Certains messages OTA (4/443 en prod) arrivent avec un body contenant du
// balisage HTML brut (ex. « <a href="https://a0.muscache.com/im/pictures-signe… »)
// qui polluait l'extrait tronqué des listes (InboxPanel/ThreadRow et liste
// proprio). Ce helper PUR (0 I/O, 0 DOM) dérive un extrait lisible AU RENDU :
//   1. suppression des blocs <style>/<script> et commentaires HTML,
//   2. strip des balises HTML — heuristique CONSERVATRICE : une balise = « < »
//      suivi d'un nom de balise ([a-z][a-z0-9-]*) puis « > » (attributs
//      éventuels). Un « < » mathématique (« 5 < 10 », « <100 € », « prix < base »)
//      n'est JAMAIS mutilé (pas de nom de balise ou pas de fermeture « > ») ;
//   3. décodage des entités HTML (&amp; &eacute; &#233; &#x27; …) — APRÈS le
//      strip, pour qu'un « &lt;script&gt; » volontairement échappé reste du
//      texte et ne soit jamais retiré ;
//   4. effondrement des espaces (dont &nbsp;) ;
//   5. troncature propre (bord de mot si possible + ellipse « … »).
//
// ⚠️ GARDE-FOUS (ne pas violer) :
//   • Le body STOCKÉ (inbox_messages.body) est la SOURCE DE VÉRITÉ : il n'est
//     JAMAIS modifié — helper d'AFFICHAGE des extraits de liste uniquement.
//   • La conversation ouverte (ThreadDisplay) a SON propre traitement
//     (lib/inbox-media.js extractOtaImages + échappement React) : ne pas
//     brancher ce helper là-bas.
//   • Lisibilité, PAS sécurité : React échappe déjà tout à l'affichage — ce
//     strip n'est pas une défense XSS et ne doit jamais en devenir une
//     (aucun dangerouslySetInnerHTML, ici comme en aval).

// Blocs dont le CONTENU est illisible (CSS/JS des emails HTML) : retirés
// entièrement, balises ET contenu.
const BLOCK_RE = /<(style|script)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
const COMMENT_RE = /<!--[\s\S]*?-->/g;

// Balise HTML « plausible » : </nom>, <nom>, <nom attr="…" …>, <nom … />.
// CONSERVATEUR À DESSEIN : chaque attribut doit porter un « = » (nom=valeur,
// quotée ou nue) — c'est le cas de tout le HTML d'email réel (href=, style=,
// src=…). Conséquences voulues :
//   • « 5 < 10 », « <100 € », « a < b » ne matchent jamais (pas de nom de
//     balise, ou pas de « > » fermant) ;
//   • « a<b et c>d » (double comparaison) ne matche pas non plus (« et »/« d »
//     ne sont pas des attributs nom=valeur) → texte préservé ;
//   • une balise à attribut booléen nu (`<div data-foo>`, rare en email)
//     resterait visible — faux négatif inoffensif, préféré à toute mutilation.
const TAG_ATTR = `\\s+[a-zA-Z-][a-zA-Z0-9:._-]*=(?:"[^"]*"|'[^']*'|[^\\s<>]+)`;
const TAG_RE = new RegExp(
  `<\\/?[a-zA-Z][a-zA-Z0-9-]*(?:${TAG_ATTR})*\\s*\\/?>`,
  "g",
);

// Balise TRONQUÉE en fin de chaîne (body coupé en amont, pas de « > ») —
// retirée UNIQUEMENT sur PREUVE de tag-ness : balise fermante (</nom…) ou
// premier attribut nom=valeur immédiatement après le nom de balise
// (ex. `<a href="https://…`). Un « x < y » ou « a<b alors x=1 » mathématique
// final ne matche pas (l'attribut doit suivre directement le nom de balise).
const TRUNCATED_TAG_RE =
  /(?:<\/[a-zA-Z][^<>]*|<[a-zA-Z][a-zA-Z0-9-]*\s+[a-zA-Z-][a-zA-Z0-9:._-]*=[^<>]*)$/;

// Entités nommées courantes des emails OTA/FR. Table volontairement bornée :
// une entité inconnue est laissée telle quelle (honnête, jamais mutilée).
const NAMED_ENTITIES = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ",
  eacute: "é", egrave: "è", ecirc: "ê", euml: "ë",
  agrave: "à", acirc: "â", ccedil: "ç",
  icirc: "î", iuml: "ï", ocirc: "ô", ouml: "ö",
  ugrave: "ù", ucirc: "û", uuml: "ü", oelig: "œ",
  euro: "€", copy: "©", reg: "®", deg: "°", middot: "·",
  laquo: "«", raquo: "»", hellip: "…", ndash: "–", mdash: "—",
  rsquo: "’", lsquo: "‘", rdquo: "”", ldquo: "“",
};

// Décodage entités — UNE seule passe gauche→droite (jamais de re-scan) :
// « &amp;lt; » devient le TEXTE « &lt; », pas « < » (anti double-décodage).
function decodeHtmlEntities(s) {
  return s.replace(
    /&(#[xX]?[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]*);/g,
    (match, code) => {
      if (code[0] === "#") {
        const isHex = code[1] === "x" || code[1] === "X";
        const num = parseInt(code.slice(isHex ? 2 : 1), isHex ? 16 : 10);
        // Hors plage Unicode ou non-numérique → entité laissée telle quelle.
        if (!Number.isFinite(num) || num <= 0 || num > 0x10ffff) return match;
        try {
          return String.fromCodePoint(num);
        } catch (e) {
          return match; // point de code invalide (surrogate isolé…)
        }
      }
      const key = code.toLowerCase();
      return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, key)
        ? NAMED_ENTITIES[key]
        : match;
    },
  );
}

// Troncature propre : coupe à maxLen, recule au dernier espace s'il est proche
// (≤ 20 chars du bord — jamais de mot amputé au milieu si évitable), retire la
// ponctuation pendante, ajoute « … ». Texte court → renvoyé TEL QUEL (sans …).
function truncateClean(text, maxLen) {
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(" ");
  const base = lastSpace >= maxLen - 20 ? cut.slice(0, lastSpace) : cut;
  return base.replace(/[\s,;:.!?·-]+$/, "") + "…";
}

/**
 * Dérive l'extrait de liste d'un body de message Inbox.
 * PUR — le body passé n'est jamais muté ; le body stocké jamais réécrit.
 * @param {string|null|undefined} body — body BRUT (source de vérité intacte).
 * @param {number} [maxLen=80] — longueur max de l'extrait (aligné sur
 *   l'ancien `.slice(0, 80)` des listes).
 * @returns {string} extrait texte propre (jamais null).
 */
export function deriveInboxPreview(body, maxLen = 80) {
  if (body == null) return "";
  let s = String(body);
  if (!s.trim()) return "";
  s = s
    .replace(BLOCK_RE, " ")
    .replace(COMMENT_RE, " ")
    .replace(TAG_RE, " ")
    .replace(TRUNCATED_TAG_RE, " ");
  s = decodeHtmlEntities(s);
  s = s.replace(/\s+/g, " ").trim();
  return truncateClean(s, maxLen);
}
